import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type AppEvent } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  Column,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop, mutateListUsed } from "../desktop";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { isEmployeeAccount } from "../lib/accountType";

function mergeById(base: AppEvent[], extra: AppEvent[]): AppEvent[] {
  const map = new Map<number, AppEvent>();
  for (const e of base) map.set(e.id, e);
  for (const e of extra) map.set(e.id, e);
  return [...map.values()];
}

const READY_OPTS = {
  status: "ready",
  limit: 2000,
  includeHidden: false,
} as const;

/**
 * Employee accounts (non-admin Brex users): listed only — no Station stock /
 * passkey / Non Stash routing. Detail modal still supports Open in OctoBrowser.
 */
export function EmployeePage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [listTab, setListTab] = useState<"fresh" | "used">("fresh");
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });

  const { events: localReady } = useLocalEventsQuery(
    READY_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ events: AppEvent[] }>("/api/data/events?afterId=0&limit=500")
      .then((data) => {
        if (!cancelled) {
          setHttpSeed((data.events || []).filter((e) => e.status === "ready"));
        }
      })
      .catch(() => {
        /* live merge still works */
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = desktop ? localReady : httpSeed;
  const events = useMemo(() => mergeById(seed, liveEvents), [seed, liveEvents]);

  const listed = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return events
      .filter((e) => !e.hidden && e.status === "ready" && isEmployeeAccount(e))
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        return (
          s.id.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.type.toLowerCase().includes(q)
        );
      })
      .sort(sortEventsNewestFirst);
  }, [events, filter]);

  const freshItems = useMemo(() => listed.filter((e) => !e.listUsed), [listed]);
  const usedItems = useMemo(() => listed.filter((e) => Boolean(e.listUsed)), [listed]);
  const items = listTab === "used" ? usedItems : freshItems;

  const toggleUsed = useCallback(
    (ev: AppEvent) => {
      const next = !ev.listUsed;
      void mutateListUsed(ev.id, next)
        .then(() => push(next ? "Moved to Used" : "Moved to Fresh", "ok"))
        .catch((err) =>
          push(err instanceof Error ? err.message : "Could not update used flag", "error")
        );
    },
    [push]
  );

  const meta = statusMeta(status);
  const selectedLive = selected
    ? events.find((e) => e.id === selected.id) || selected
    : null;

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <div>
          <h1>Employee</h1>
          <p className="muted page-blurb">
            Employee accounts always land here (stash is admin-only). Mark a row used
            to move it off Fresh. Open OctoBrowser from the detail popup when needed.
          </p>
        </div>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
          <span className="muted tiny">{items.length} items</span>
        </div>
      </header>

      {error && <div className="banner error">{error}</div>}

      <div className="stock-tabs" role="tablist" aria-label="Employee inventory">
        {(
          [
            ["fresh", "Fresh", freshItems.length],
            ["used", "Used", usedItems.length],
          ] as const
        ).map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={listTab === id}
            className={`stock-tab${listTab === id ? " active" : ""}`}
            onClick={() => setListTab(id)}
          >
            {label}
            <span className="stock-tab-count">{count}</span>
          </button>
        ))}
      </div>

      <div className="feed-board feed-board-single">
        <Column
          title={listTab === "used" ? "Used" : "Fresh"}
          items={items}
          empty={
            listTab === "used"
              ? "Nothing marked used yet."
              : "No fresh employee accounts."
          }
          isAdmin
          onOpen={setSelected}
          onToggleUsed={toggleUsed}
          pageSize={25}
          resetKey={`${filter}:${listTab}`}
        />
      </div>

      {selectedLive && (
        <EventDetailModal
          event={selectedLive}
          onClose={() => setSelected(null)}
          listUsedMode
          onListUsedToggled={toggleUsed}
        />
      )}
    </div>
  );
}
